import { User } from '../types';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

interface ApiKeyResponse {
  api_key: string;
  key_prefix: string;
  name: string;
  expires_at: string | null;
  message: string;
}

class AuthService {
  private baseUrl = '/api/auth';
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshPromise: Promise<AuthResponse> | null = null;
  private refreshInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Load tokens from localStorage on initialization
    this.loadTokens();
    // Start proactive token refresh
    this.startProactiveRefresh();
  }

  private loadTokens(): void {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    const expiry = localStorage.getItem('token_expiry');
    this.tokenExpiry = expiry ? parseInt(expiry) : null;
  }

  private saveTokens(response: AuthResponse): void {
    this.accessToken = response.access_token;
    this.refreshToken = response.refresh_token;
    this.tokenExpiry = Date.now() + (response.expires_in * 1000);
    
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('token_expiry', this.tokenExpiry.toString());
    localStorage.setItem('auth_token', response.access_token); // For backward compatibility
    
    // Restart proactive refresh with new tokens
    this.startProactiveRefresh();
  }

  clearTokens(): void {
    // Stop proactive refresh
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('auth_token');
    
    // Clear Label Studio session flags
    sessionStorage.removeItem('labelstudio_user_synced');
    sessionStorage.removeItem('labelstudio_synced_user');
  }

  async refreshAccessToken(): Promise<AuthResponse> {
    // If refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = fetch(`${this.baseUrl}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: this.refreshToken,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to refresh token');
        }
        const data: AuthResponse = await response.json();
        this.saveTokens(data);
        return data;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async getValidToken(): Promise<string | null> {
    // Reload tokens in case they were updated elsewhere
    this.loadTokens();
    
    // Check if token exists and is still valid
    const now = Date.now();
    const expiryCheck = this.tokenExpiry ? this.tokenExpiry - 60000 : 0;
    
    if (this.accessToken && this.tokenExpiry && now < expiryCheck) {
      return this.accessToken;
    }

    // Try to refresh the token
    if (this.refreshToken) {
      try {
        await this.refreshAccessToken();
        return this.accessToken;
      } catch (error) {
        this.clearTokens();
        return null;
      }
    }

    return null;
  }

  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await fetch(`${this.baseUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data: AuthResponse = await response.json();
    this.saveTokens(data);

    return {
      user: data.user,
      token: data.access_token,
    };
  }

  async register(
    data: {
      email: string;
      username: string;
      password: string;
      firstName?: string;
      lastName?: string;
      organizationName?: string;
      invitation_token?: string;
    }
  ): Promise<{ user: User; token: string }> {
    const response = await fetch(`${this.baseUrl}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: data.email,
        username: data.username,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        organization_name: data.organizationName,
        invitation_token: data.invitation_token,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      // 422 validation errors return detail as an array of {loc, msg, type}
      let message = 'Registration failed';
      if (Array.isArray(error.detail)) {
        message = error.detail.map((e: any) => e.msg || e.message || String(e)).join('; ');
      } else if (typeof error.detail === 'string') {
        message = error.detail;
      }
      throw new Error(message);
    }

    const responseData: AuthResponse = await response.json();
    this.saveTokens(responseData);

    return {
      user: responseData.user,
      token: responseData.access_token,
    };
  }

  async logout(): Promise<void> {
    try {
      const token = await this.getValidToken();
      if (token) {
        await fetch(`${this.baseUrl}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  async validateToken(token: string): Promise<User> {
    const response = await fetch(`${this.baseUrl}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Invalid token');
    }

    const user: User = await response.json();
    return user;
  }

  async getProfile(): Promise<User> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    const user: User = await response.json();
    return user;
  }

  async updateProfile(updates: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    preferences?: Record<string, any>;
  }): Promise<User> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/me`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: updates.firstName,
        last_name: updates.lastName,
        phone: updates.phone,
        preferences: updates.preferences,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    const user: User = await response.json();
    return user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change password');
    }

    // Clear tokens after password change (forces re-login)
    this.clearTokens();
  }

  async listApiKeys(): Promise<Array<{
    id: number;
    name: string;
    key_prefix: string;
    created_at: string;
    last_used: string | null;
    expires_at: string | null;
  }>> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/api-keys`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch API keys');
    }

    const data = await response.json();
    return data.api_keys;
  }

  async createApiKey(
    name: string,
    expiresInDays?: number,
    permissions?: string[]
  ): Promise<ApiKeyResponse> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/api-keys`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        expires_in_days: expiresInDays,
        permissions,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create API key');
    }

    const data: ApiKeyResponse = await response.json();
    return data;
  }

  async revokeApiKey(keyId: number): Promise<void> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to revoke API key');
    }
  }

  async acceptInvitation(token: string): Promise<void> {
    const authToken = await this.getValidToken();
    
    const response = await fetch('/api/invitations/accept', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to accept invitation');
    }
  }

  getAuthHeader(): { Authorization: string } | {} {
    // Reload tokens to ensure we have the latest
    this.loadTokens();
    
    if (this.accessToken) {
      return { Authorization: `Bearer ${this.accessToken}` };
    }
    // Fallback to old method for backward compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getAuthHeaderAsync(): Promise<{ Authorization: string } | {}> {
    const token = await this.getValidToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Label Studio Account Management
  async getLabelStudioAccountStatus(): Promise<{
    exists: boolean;
    email?: string;
    username?: string;
    is_active?: boolean;
    message?: string;
  }> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/labelstudio/status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to check Label Studio account status');
    }

    return await response.json();
  }

  async createLabelStudioAccount(password: string): Promise<void> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/labelstudio/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create Label Studio account');
    }
  }

  async changeLabelStudioPassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/labelstudio/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change Label Studio password');
    }
  }

  async syncLabelStudioPassword(password: string): Promise<void> {
    const token = await this.getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/labelstudio/sync-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to sync Label Studio password');
    }
  }

  private startProactiveRefresh(): void {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Check token every 5 minutes
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, 5 * 60 * 1000) as any; // 5 minutes

    // Also check immediately
    this.checkAndRefreshToken();
  }

  private async checkAndRefreshToken(): Promise<void> {
    try {
      // Reload tokens to ensure we have the latest
      this.loadTokens();

      if (!this.accessToken || !this.refreshToken || !this.tokenExpiry) {
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = this.tokenExpiry - now;

      // Refresh if less than 10 minutes remaining
      if (timeUntilExpiry < 10 * 60 * 1000) {
        console.log('Proactive token refresh: Token expiring soon, refreshing...');
        await this.refreshAccessToken();
      } else if (timeUntilExpiry < 15 * 60 * 1000) {
        // Warn user when 15 minutes remaining
        console.log('Token expiring in less than 15 minutes');
      }
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      // Don't logout on proactive refresh failure
      // The regular request flow will handle it
    }
  }

}

export const authService = new AuthService();